package com.evoapps.evoshift.ui.roster

import com.evoapps.evoshift.widgets.ProvideDialogColors
import com.evoapps.evoshift.widgets.dialogKeys
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CalendarToday
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.ContentPaste
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Event
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material.icons.filled.PersonRemove
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.ContextMenuItem
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.context_close_day
import org.jetbrains.compose.resources.stringResource

/*
 * SH-98: the roster builder in a laptop window (Googlebook, Chromebooks, a desktop browser): a
 * grid that uses the width, right-click menus on the cells and the day docked beside the grid.
 */

/** The narrowest day column (a tablet's): below it the month scrolls sideways. */
internal val GridCellMinWidth = 64.dp

/** The widest a day column grows on a very wide window: wider only spreads the shifts thin. */
internal val GridCellMaxWidth = 120.dp

/** A person's row in the grid. */
internal val GridRowHeight = 84.dp

/** SH-169: the narrowest a zoomed-out day column gets. */
internal val GridCellZoomedMinWidth = 40.dp

/** The docked day pane beside the grid on laptop-wide windows. */
internal val DayPaneWidth = 380.dp

/** SH-105: the pane beside the grid on expanded windows (tablets in landscape, a flat iPhone Duo). */
internal val DayPaneWidthExpanded = 320.dp

/** SH-105: the month's overview beside the grid on a very wide window: the whole month still fits. */
internal val OverviewPaneWidthXl = 240.dp

/**
 * A day column's width for [days] days in [available] width: the whole month when it fits at
 * [GridCellMinWidth] or more (capped at [GridCellMaxWidth]), else the narrowest (it scrolls).
 */
internal fun gridCellWidth(available: Dp, days: Int): Dp {
    if (days <= 0) return GridCellMinWidth
    return (available / days).coerceIn(GridCellMinWidth, GridCellMaxWidth)
}

/** The texts of a grid cell's right-click menu, resolved once per screen. */
internal class ShiftCellMenuLabels(val assign: String, val edit: String, val unassign: String, val showDay: String)

/** "08:00–16:00": a shift's planned times. */
internal fun Shift.plannedRange(): String = "${plannedTime.start.substring(11, 16)}–${plannedTime.end.substring(11, 16)}"

/**
 * A person's cell's right-click menu: the same actions as a tap (assign a shift: the sheet) and
 * the day's sheet (edit a shift), plus the sheet's unassign; a read-only month only shows the day.
 */
internal fun shiftCellMenu(
    labels: ShiftCellMenuLabels,
    shifts: List<Shift>,
    editable: Boolean,
    onAssign: () -> Unit,
    onEdit: (Shift) -> Unit,
    onUnassign: (String) -> Unit,
    onShowDay: () -> Unit
): List<ContextMenuItem> = buildList {
    if (editable) {
        add(ContextMenuItem(labels.assign, onAssign, Icons.Default.PersonAdd))
        shifts.forEach { shift ->
            add(ContextMenuItem("${labels.edit} ${shift.plannedRange()}", { onEdit(shift) }, Icons.Default.Edit))
        }
        shifts.filter { it.userId != null }.forEach { shift ->
            add(ContextMenuItem("${labels.unassign} ${shift.plannedRange()}", { onUnassign(shift.id) }, Icons.Default.PersonRemove, destructive = true))
        }
    }
    add(ContextMenuItem(labels.showDay, onShowDay, Icons.Default.CalendarToday))
}

/** SH-171: the labels of a day header's right-click menu, read once for the whole grid. */
internal class DayHeaderMenuLabels(
    val copyThis: String,
    val copySelected: String,
    val pasteHere: String,
    val pasteSelected: String,
    val showDay: String,
    /** SH-227: "Add event…" and "Edit "Open'er"…". */
    val addEvent: String = "",
    val editEvent: (String) -> String = { it }
)

/**
 * SH-171: a day header's right-click menu: copy this day or the selected days to paste from
 * another header (as Ctrl/Cmd+C; the copy tool itself is the top bar's, SH-215), paste
 * the copied days from this day or from each selected day ([canPaste]: something is copied), and
 * the day's pane. [onSelection]: the header is one of several selected. Copying needs [canCopy].
 */
internal fun dayHeaderMenu(
    labels: DayHeaderMenuLabels,
    canCopy: Boolean,
    onSelection: Boolean,
    hasShifts: Boolean,
    canPaste: Boolean,
    onCopy: () -> Unit,
    onPaste: () -> Unit,
    onShowDay: () -> Unit,
    /** SH-227: the day's events typed in (each "Edit …"), and "Add event…" ([onAddEvent] null: not offered). */
    events: List<com.evoapps.evoshift.domain.model.LocalEvent> = emptyList(),
    onAddEvent: (() -> Unit)? = null,
    onEditEvent: (String) -> Unit = {}
): List<ContextMenuItem> = buildList {
    if (canCopy) {
        add(ContextMenuItem(if (onSelection) labels.copySelected else labels.copyThis, onCopy, Icons.Default.ContentCopy, enabled = hasShifts))
        if (canPaste) add(ContextMenuItem(if (onSelection) labels.pasteSelected else labels.pasteHere, onPaste, Icons.Default.ContentPaste))
    }
    if (onAddEvent != null) {
        add(ContextMenuItem(labels.addEvent, onAddEvent, Icons.Default.Event))
        events.forEach { event -> add(ContextMenuItem(labels.editEvent(event.name), { onEditEvent(event.id) }, Icons.Default.Edit)) }
    }
    add(ContextMenuItem(labels.showDay, onShowDay, Icons.Default.CalendarToday))
}

/** Test tag of the docked day pane. */
const val DAY_PANE_TAG = "roster_day_pane"

/**
 * The day's shifts docked beside the grid on a laptop-wide window (a sheet on narrower ones): the
 * sheet's navy look, colours ([ProvideDialogColors]) and content under [title], a close button; Esc closes it too.
 */
@Composable
internal fun DayDetailsPane(
    title: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit
) {
    Column(
        modifier
            .background(DarkMetallicBackgroundBrush)
            // Takes the focus when it opens: Esc closes it, Tab walks its shifts.
            .dialogKeys(onEscape = onClose, onEnter = null)
            .testTag(DAY_PANE_TAG)
    ) {
        Row(
            Modifier.fillMaxWidth().padding(start = 20.dp, end = 8.dp, top = 12.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.CalendarToday, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(12.dp))
            Text(
                title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = LightGold,
                modifier = Modifier.weight(1f)
            )
            IconButton(onClick = onClose, modifier = Modifier.handCursor()) {
                Icon(Icons.Default.Close, contentDescription = stringResource(Res.string.context_close_day), tint = Color.White)
            }
        }
        Column(
            Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(start = 20.dp, end = 20.dp, top = 8.dp, bottom = 32.dp)
        ) {
            // SH-203: the sheet's colours too (as EvoBottomSheet), so what reads LocalOnColours
            // (the team's availability, the missing-pay banner) is light on the navy, not the cream screen's.
            ProvideDialogColors(content)
        }
    }
}
